<template>
  <div v-if="$apollo.loading">
    <spinner />
  </div>
  <div v-else class="container">
    <div class="container-header">
      <main-button flat @click="goBack">
        <v-icon class="arrow-back">arrow_back</v-icon>
        <span>back to all promo codes</span>
      </main-button>
      <main-button
        class="delete-button"
        color="error"
        flat
        text="DELETE"
        @click="showDeletePromoCodeDialog = true"
      />
    </div>
    <div class="info-container">
      <div class="info-container-block text-block">
        <p>Promo code name</p>
        <h5>{{ promoCode.name }}</h5>
      </div>
      <div class="info-container-block text-block">
        <p>Unique Promo code</p>
        <h5>{{ promoCode.code }}</h5>
      </div>
      <div class="info-container-block text-block">
        <p>Sold quantity</p>
        <h5>{{ calculateTotalSoldQuantity() }}</h5>
      </div>
      <div class="info-container-block text-block">
        <p>Initial quantity</p>
        <h5>{{ calculateTotalInitialQuantity() }}</h5>
      </div>
      <div class="info-container-block date-block">
        <p>Start date</p>
        <h5>{{ moment(promoCode.from).format('DD.MM.YYYY.') }}</h5>
      </div>
      <div class="info-container-block date-block">
        <p>End date</p>
        <h5>{{ moment(promoCode.to).format('DD.MM.YYYY.') }}</h5>
      </div>
      <div class="info-container-block text-block">
        <p>Promo code type</p>
        <h5>{{ discountTypes[promoCode.discountType] }}</h5>
      </div>
      <div class="info-container-block number-block">
        <p>Discount</p>
        <h5>
          {{
            promoCode.discountPercentage
              ? `${promoCode.discountPercentage}%`
              : `${promoCode.discountAmount} ${promoCode.currency}`
          }}
        </h5>
      </div>
    </div>
    <div class="actions-header">
      <v-text-field
        label="Search Event"
        append-icon="search"
        box
        style="max-width: 24.063rem"
        @change="handleSearchChange($event)"
      />
      <main-button
        :disabled="allEvents.length === 0"
        class="add-new-event-button"
        flat
        @click="showNewEventDialog = true"
      >
        <v-icon class="edit-icon">edit</v-icon>
        <span>Add new event</span>
      </main-button>
    </div>
    <div class="table-container">
      <selected-items-header
        v-if="selected.length"
        :selected="selected"
        name="event"
        @clear="selected = []"
        @delete="showDeleteEventPromoCodeDialog = true"
      />
      <table-list
        :selected.sync="selected"
        :organization-slug="organizationSlug"
        :attendees="orderItems"
        body-style="grid-auto-columns: 50px minmax(50px, 150px) !important;"
        :visible-fields="visibleFields"
        :attendee-fields="fields"
        :pagination="pagination"
        @sort="handleSort($event)"
      />
      <table-pagination
        :pagination="pagination"
        :total-attendees="totalOrderItemsCount"
        @change="handlePagination($event)"
        @changePerPage="handlePagination($event)"
      />
    </div>
    <delete-dialog
      :content="`Are you sure you want to delete this promo code? This operation cannot be undone.`"
      :on-delete="deletePromoCode"
      :show.sync="showDeletePromoCodeDialog"
      title="Delete promo code"
    />
    <delete-dialog
      :content="`Are you sure you want to remove selected tickets? This operation cannot be undone.`"
      :on-delete="deleteEventsPromoCode"
      :show.sync="showDeleteEventPromoCodeDialog"
      title="Remove tickets from promo code"
    />
    <v-dialog v-model="showNewEventDialog" max-width="512" persistent>
      <div class="modal-container">
        <div class="modal-header">
          <div class="modal-text-container">
            <h1 class="modal-text-title">Add new events</h1>
          </div>
          <v-icon color="#979797" class="close-icon" @click="cancel">close</v-icon>
        </div>
        <div class="main-container">
          <div class="form-container">
            <select-field-multiple
              v-model="newEventForm.eventIds"
              :validator="$v.newEventForm.eventIds"
              label="Event*"
              :items="allEvents"
              item-render="title"
              item-select="id"
            />
            <select-field-multiple
              v-if="performances.length"
              v-model="newEventForm.performanceIds"
              :validator="$v.newEventForm.performanceIds"
              :error-messages="{
                selectedPerformances: 'Please select performances for all selected events',
              }"
              label="Performance*"
              :items="performances"
              item-render="title"
              item-select="id"
            />
            <select-field-multiple
              v-model="newEventForm.orderItemIds"
              :disabled="!newEventForm.eventIds.length"
              :validator="$v.newEventForm.orderItemIds"
              :error-messages="{
                selectedOrderItems: 'Please select tickets for all selected events',
              }"
              label="Ticket*"
              :items="allOrderItems"
              item-render="name"
              item-select="id"
            />
          </div>
          <div class="button-container">
            <main-button @click="handleUpdate">update</main-button>
          </div>
        </div>
      </div>
    </v-dialog>
    <simple-snackbar v-bind.sync="snackbar" />
  </div>
</template>

<script>
import moment from 'moment'
import selectedItemsHeader from '../../../components/tickets/selected-items-header.vue'
import deleteDialog from '../../shared/dialogs/delete-dialog.vue'
import tableList from '../../accreditations/attendee-table/list.vue'
import {
  GET_PROMO_CODE,
  REMOVE_TICKETS_FROM_PROMO_CODE,
  GET_PROMO_CODES,
  ADD_TICKETS_TO_PROMO_CODE,
  DELETE_PROMO_CODE,
} from '../../../graphql/promoCode.graphql'
import { GET_EVENTS } from '../../../graphql/event.graphql'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { compact, difference, intersection, isEqual, uniq, uniqBy } from 'lodash'
import simpleSnackbar from '../../shared/snackbars/simple-snackbar.vue'
import tablePagination from '@/components/accreditations/attendee-table/pagination.vue'

const discountTypes = Object.freeze({
  PERCENTAGE: 'Discount percentage',
  AMOUNT: 'Discount amount',
})

const selectedPerformances = (eventTemplates, allPerformances) => (val, model) => {
  const selectedEventTemplatesIds = eventTemplates
    .filter(et => model.eventIds.includes(et.id))
    .map(et => et.id)

  const selectedPerformanceEventTemplatesIds = uniq(
    allPerformances.filter(p => val.includes(p.id)).map(p => p.eventTemplate.id),
  )

  return isEqual(selectedEventTemplatesIds, selectedPerformanceEventTemplatesIds)
}

const selectedOrderItems = (eventsWithoutTemplate, allPerformances) => (val, model) => {
  const allEvents = [...eventsWithoutTemplate, ...allPerformances]

  const selectedEventsIds = [
    ...model.eventIds.filter(ei => allEvents.map(e => e.id).includes(ei)),
    ...model.performanceIds,
  ].sort()

  const selectedOrderItemsEventsIds = allEvents
    .filter(e => e.orderItems.map(oi => oi.id).some(el => val.includes(el)))
    .map(e => e.id)
    .sort()

  return isEqual(selectedOrderItemsEventsIds, selectedEventsIds)
}

export default {
  name: 'promo-code-info-container',
  components: {
    tablePagination,
    selectedItemsHeader,
    tableList,
    deleteDialog,
    simpleSnackbar,
  },
  props: {
    organizationSlug: String,
    promoCodeId: [Number, String],
  },
  data() {
    const { pagination } = this.getInitialFromQueryParams()

    return {
      selected: [],
      showDeletePromoCodeDialog: false,
      showDeleteEventPromoCodeDialog: false,
      showNewEventDialog: false,
      promoCode: null,
      orderItems: [],
      fields: {},
      visibleFields: ['Event name', 'Date', 'Ticket name', 'Sold quantity'],
      snackbar: {},
      pagination,
      moment,
      discountTypes,
      newEventForm: { eventIds: [], performanceIds: [], orderItemIds: [] },
      allEvents: [],
      eventTemplates: [],
      eventsWithoutTemplate: [],
      allPerformances: [],
      searchTerm: null,
      totalOrderItemsCount: 0,
    }
  },
  validations() {
    return {
      newEventForm: {
        eventIds: { required },
        performanceIds: {
          required: requiredIf(
            val =>
              intersection(
                this.eventTemplates.map(et => et.id),
                val.eventIds,
              ).length,
          ),
          selectedPerformances: selectedPerformances(this.eventTemplates, this.allPerformances),
        },
        orderItemIds: {
          required,
          selectedOrderItems: selectedOrderItems(this.eventsWithoutTemplate, this.allPerformances),
        },
      },
    }
  },
  apollo: {
    getPromoCode: {
      query: GET_PROMO_CODE,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
          promoCodeId: parseInt(this.promoCodeId),
          pagination: {
            ...this.pagination,
            per_page: this.pagination.per_page === 'All' ? -1 : this.pagination.per_page,
            sortBy: this.transformSortByString(this.pagination.sortBy),
          },
          searchTerm: this.searchTerm,
        }
      },
      async result({ data: { getPromoCode }, loading }) {
        if (!loading) {
          if (!getPromoCode) {
            return this.goBack()
          }

          this.promoCode = getPromoCode.promoCode
          this.orderItems = getPromoCode.promoCode.orderItems.map(oi => ({
            id: oi.id,
            'event name': oi.event.title,
            date: moment(oi.event.from).format('DD.MM.YY HH:mm'),
            name: oi.name,
            quantity: oi.OrderItemPromoCode.quantity ? oi.OrderItemPromoCode.quantity : '-',
            'initial quantity': oi.OrderItemPromoCode.initialQuantity
              ? oi.OrderItemPromoCode.initialQuantity
              : '-',
            'sold quantity': oi.OrderItemPromoCode.soldQuantity || 0,
            unlimited: oi.OrderItemPromoCode.unlimited ? 'Yes' : 'No',
          }))

          this.totalOrderItemsCount = getPromoCode.count

          const {
            data: { getEvents },
          } = await this.$apollo.query({
            query: GET_EVENTS,
            variables: { organizationSlug: this.organizationSlug },
          })

          if (!getEvents) {
            this.goBack()
          } else {
            const liveEvents = getEvents.filter(
              e =>
                moment(e.to).isAfter(moment()) &&
                difference(
                  e.orderItems.map(oi => oi.id),
                  this.promoCode.orderItems.filter(oi => oi.event.id === e.id).map(oi => oi.id),
                ).length > 0,
            )
            const eventsWithoutTemplate = liveEvents.filter(e => !e.eventTemplate)
            const eventTemplates = uniqBy(compact(liveEvents.map(e => e.eventTemplate)), 'id')

            this.allEvents = [...eventsWithoutTemplate, ...eventTemplates]
            this.eventsWithoutTemplate = eventsWithoutTemplate
            this.eventTemplates = eventTemplates
            this.allPerformances = liveEvents.filter(e => e.eventTemplate)
          }
        }
      },
    },
  },
  computed: {
    performances() {
      const newPerformances = this.allPerformances
        .filter(p => this.newEventForm.eventIds.includes(p.eventTemplate.id))
        .map(p => ({
          ...p,
          title: `${p.eventTemplate.title} - ${moment(p.from).format('DD/MM/YYYY. HH:mm')}`,
        }))

      if (newPerformances.length === 0) {
        this.$v.newEventForm.performanceIds.$reset()
      }

      return newPerformances
    },
    allOrderItems() {
      return compact(
        [...this.allEvents, ...this.allPerformances]
          .filter(e =>
            [
              ...this.newEventForm.eventIds,
              ...(this.newEventForm.performanceIds ? this.newEventForm.performanceIds : []),
            ].includes(e.id),
          )
          .flatMap(e =>
            e.orderItems
              ? e.orderItems
                  .filter(oi => !this.orderItems.map(oi => oi.id).includes(oi.id))
                  .map(oi => ({
                    ...oi,
                    name: `${e.title}${
                      e.eventTemplate ? ` (${moment(e.from).format('DD/MM/YYYY. HH:mm')})` : ''
                    } - ${oi.name}`,
                  }))
              : [],
          ),
      )
    },
  },
  watch: {
    newEventForm: {
      handler(val) {
        const newForm = { ...val }

        newForm.performanceIds = intersection(
          newForm.performanceIds,
          this.performances
            .filter(p => newForm.eventIds.includes(p.eventTemplate.id))
            .map(p => p.id),
        )
        newForm.orderItemIds = intersection(
          newForm.orderItemIds,
          this.allOrderItems
            .filter(oi =>
              [
                ...newForm.eventIds,
                ...(newForm.performanceIds ? newForm.performanceIds : []),
              ].includes(oi.eventId),
            )
            .map(p => p.id),
        )
        if (!isEqual(this.newEventForm, newForm)) {
          this.newEventForm = newForm
        }
      },
      deep: true,
    },
  },
  methods: {
    cancel() {
      this.$v.$reset()
      this.newEventForm = { eventIds: [], performanceIds: [], orderItemIds: [] }
      this.showNewEventDialog = false
    },
    async handleUpdate() {
      this.$v.$touch()

      if (!this.$v.$invalid) {
        const { errors } = await this.$apollo.mutate({
          mutation: ADD_TICKETS_TO_PROMO_CODE,
          variables: {
            organizationSlug: this.organizationSlug,
            promoCodeId: this.promoCode.id,
            orderItemIds: this.newEventForm.orderItemIds.map(id => parseInt(id)),
          },
          awaitRefetchQueries: true,
          refetchQueries: ['GetPromoCode'],
        })

        if (errors) {
          this.snackbar = {
            open: true,
            messageProp: 'Oops, something went wrong!',
            color: 'error',
          }
        } else {
          this.snackbar = {
            open: true,
            messageProp: 'Successfully created promo code!',
            color: 'success',
          }

          this.showNewEventDialog = false
          this.$v.$reset()
          this.newEventForm = {
            eventIds: [],
            performanceIds: [],
            orderItemIds: [],
          }
        }
      }
    },
    async handlePagination({ page, per_page }) {
      const { page: currentPage, per_page: currentPerPage } = this.pagination
      if (currentPage > page && currentPage === 1) return
      if (page > currentPage && !(this.totalOrderItemsCount - currentPerPage * currentPage > 0))
        return

      this.pagination = {
        ...this.pagination,
        per_page,
        page,
      }

      this.addPaginationToRoute(this.pagination)
    },
    async handleSort(sortBy) {
      const { per_page, page, sortBy: thisSortBy, descending } = this.pagination
      const pagination = {
        page,
        per_page,
        sortBy,
        descending: sortBy === thisSortBy ? !descending : false,
      }
      this.addPaginationToRoute(pagination)
      this.pagination = pagination
    },
    getInitialFromQueryParams() {
      const { per_page, page, sortBy, descending } = this.$route.query

      return {
        pagination: {
          page: parseInt(page, 10) || 1,
          per_page: parseInt(per_page, 10) || 'All',
          sortBy: sortBy,
          descending: descending ? !!descending : false,
        },
      }
    },
    async addPaginationToRoute({ page, per_page, sortBy, descending }) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          per_page: parseInt(per_page, 10) || 'All',
          page,
          sortBy,
          descending: descending ? 1 : 0,
        },
      })
    },
    async deletePromoCode() {
      const { errors } = await this.$apollo.mutate({
        mutation: DELETE_PROMO_CODE,
        awaitRefetchQueries: true,
        refetchQueries: [
          { query: GET_PROMO_CODES, variables: { organizationSlug: this.organizationSlug } },
        ],
        variables: {
          organizationSlug: this.organizationSlug,
          promoCodeId: parseInt(this.promoCode.id),
        },
      })

      if (errors) {
        this.snackbar = {
          open: true,
          messageProp: 'Oops, something went wrong!',
          color: 'error',
        }
      } else {
        this.snackbar = {
          open: true,
          messageProp: 'Successfully deleted promo code!',
          color: 'success',
        }
        this.goBack()
      }
    },
    async deleteEventsPromoCode() {
      const { data, errors } = await this.$apollo.mutate({
        mutation: REMOVE_TICKETS_FROM_PROMO_CODE,
        variables: {
          organizationSlug: this.organizationSlug,
          promoCodeId: parseInt(this.promoCode.id),
          orderItemIds: this.selected.map(id => parseInt(id)),
        },
      })

      if (errors) {
        this.snackbar = {
          open: true,
          messageProp: 'Oops, something went wrong!',
          color: 'error',
        }
      } else {
        this.snackbar = {
          open: true,
          messageProp: 'Successfully removed event from promo code!',
          color: 'success',
        }
      }
      if (data.removeTicketsFromPromoCode && this.orderItems.length > this.selected.length) {
        await this.$apollo.queries.getPromoCode.refetch()
      } else {
        this.goBack()
      }
      this.selected = []
    },
    calculateTotalSoldQuantity() {
      return this.orderItems.reduce((sum, curr) => {
        return sum + curr['sold quantity'] || 0
      }, 0)
    },
    calculateTotalInitialQuantity() {
      if (this.orderItems.some(item => item.unlimited === 'Yes')) {
        return 'unlimited'
      }

      return this.orderItems.reduce((sum, curr) => {
        return sum + curr['initial quantity'] || 0
      }, 0)
    },
    transformSortByString(sortBy) {
      if (!sortBy) {
        return undefined
      }

      return sortBy === 'Event name'
        ? 'title'
        : sortBy === 'Date'
        ? 'from'
        : sortBy === 'Ticket name'
        ? 'name'
        : sortBy.slice(0, 1).toLowerCase() + sortBy.slice(1)
    },
    async handleSearchChange(input) {
      this.searchTerm = input
      await this.$apollo.queries.getPromoCode.refetch()
    },
  },
}
</script>

<style scoped>
.container {
  padding: 0 !important;
  margin: 0 !important;
  display: flex;
  flex-direction: column;
  max-width: 68rem;
}
.container-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
}
.info-container {
  display: flex;
  flex-direction: row;
  margin-top: 16px;
  padding: 16px;
  gap: 24px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background-color: #f5f5f5;
  flex-wrap: wrap;
}
.info-container-block {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.text-block {
  flex: 1 0 0;
}
.date-block {
  width: 108px;
}
.number-block {
  width: 65px;
}
.info-container-block p {
  font-size: 14px;
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  color: #666666;
  margin: 0%;
  padding: 0%;
}
.info-container-block h5 {
  font-size: 18px;
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  color: #666666;
  margin: 0%;
  padding: 0%;
}
.info-container .info-container-block:first-child h5 {
  font-weight: 700;
}
.table-container {
  margin-top: 16px;
}
.actions-header {
  margin-top: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
}
.edit-icon {
  margin-right: 0.5rem;
}
.modal-container {
  background: #ffffff;
  box-shadow: 0px 0px 24px rgba(51, 51, 51, 0.2);
  border-radius: 12px;
  padding: 24px 24px 32px 24px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.modal-header {
  display: flex;
  align-items: baseline;
}
.modal-text-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.modal-text-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #0277bd;
}
.close-icon {
  cursor: pointer;
  user-select: none;
}
.main-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.form-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 0%;
}
.button-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
}
</style>
